import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import { Chip, Typography } from "@mui/material";
import {
  ADD_USER_TYPE,
  AddMembersContext,
  ApiErrorHandler,
  IAddMembersContext,
  SelectedUserType,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext } from "react";
import Icon from "../../common/Icon/Icon";
import {
  AddedMembersContainer,
  AddMemberButton,
  ButtonWrapper,
  ChipsContainer,
  ChipsWrapper,
} from "../AddMember.style";

type AddMemberChipsProps = {
  isLoading: boolean;
  buttonLabel?: string;
  minMembers?: number;
  addMembersAndCloseDialog?: () => Promise<void>;
  onAddMemberCallback: (selectedMembers: any) => void;
};

const AddMemberChips = ({
  isLoading,
  buttonLabel,
  minMembers,
  addMembersAndCloseDialog,
  onAddMemberCallback,
}: AddMemberChipsProps) => {
  const { selectedUsers, onRemoveUser, onAddMembers }: IAddMembersContext =
    useContext(AddMembersContext);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const tNsDrawer = (key: string) =>
    t(key, { ns: "drawerHeader", minMemberCount: minMembers });

  const onAddMembersClick = async () => {
    try {
      addMembersAndCloseDialog
        ? await addMembersAndCloseDialog()
        : await onAddMembers(onAddMemberCallback, minMembers);
    } catch (e: any) {
      if (e?.message === "minMemberCountError") {
        enqueueSnackbar(tNsDrawer("minMembers"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  return (
    selectedUsers.length > 0 && (
      <AddedMembersContainer>
        <ChipsWrapper>
          <ChipsContainer>
            {selectedUsers.map((user: SelectedUserType) => {
              return (
                <Chip
                  data-testid="selected-user-list-item"
                  key={user.id}
                  icon={
                    user.type === ADD_USER_TYPE.GROUP ? (
                      <GroupsIcon data-testid="selected-user-list-item-group-icon" />
                    ) : (
                      <PersonIcon data-testid="selected-user-list-item-user-icon" />
                    )
                  }
                  label={user.title}
                  onDelete={() => onRemoveUser(user.id)}
                />
              );
            })}
          </ChipsContainer>
        </ChipsWrapper>
        <ButtonWrapper>
          <Typography>Selected: {selectedUsers.length}</Typography>
          <AddMemberButton
            data-testid="selected-members-btn"
            loading={isLoading}
            loadingPosition="start"
            startIcon={
              <Icon icon={<SaveIcon />} color={"primaryContrastText"} />
            }
            variant="contained"
            onClick={onAddMembersClick}
            size="small"
          >
            {isLoading
              ? t("buttonLabels.adding")
              : buttonLabel || tNsDrawer("addMembers")}
          </AddMemberButton>
        </ButtonWrapper>
      </AddedMembersContainer>
    )
  );
};

export default AddMemberChips;
